import { useCallback, useEffect, useRef, useState } from "react";
import { escalateTicketToSupport, getTroubleshootingSteps } from "../../api/ticket.ts";
import styles from "../../styles/formPage.module.scss";

/**
 * Pre-escalation checklist: the user marks what they already tried (steps come from the ticket's
 * issue category) and can add a note. Support gets this in their ticket, so it must be filled
 * in (at least one step or a note) before the ticket can be sent — the server enforces the same rule.
 */
export function EscalatePanel({
  ticketId,
  onCancel,
  onEscalated,
}: {
  ticketId: number;
  onCancel: () => void;
  onEscalated: () => void | Promise<void>;
}) {
  const [steps, setSteps] = useState<string[] | null>(null);
  const [tried, setTried] = useState<boolean[]>([]);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadedFor = useRef<number | null>(null);
  useEffect(() => {
    if (loadedFor.current === ticketId) return;
    loadedFor.current = ticketId;
    getTroubleshootingSteps(ticketId)
      .then((r) => {
        const list = !r.bError && r.oData ? r.oData : [];
        setSteps(list);
        setTried(list.map(() => false));
      })
      .catch(() => setSteps([]));
  }, [ticketId]);

  const canSend = !sending && (tried.some(Boolean) || note.trim().length > 0);

  const handleSend = useCallback(async () => {
    if (!steps) return;
    setSending(true);
    setError(null);
    try {
      const answers = steps.map((text, i) => ({ Text: text, Tried: tried[i] ?? false }));
      const r = await escalateTicketToSupport(ticketId, answers, note.trim());
      if (!r.bError && r.oData) {
        await onEscalated();
      } else {
        setError(r.sMessage ?? "Escalation failed.");
      }
    } catch (e: unknown) {
      setError(String(e));
    } finally {
      setSending(false);
    }
  }, [steps, tried, note, ticketId, onEscalated]);

  return (
    <div
      style={{
        border: "1px solid var(--color-border-default)",
        borderRadius: "var(--radius-2)",
        padding: "var(--space-3)",
        marginBottom: "var(--space-3)",
      }}
    >
      <h4 className={styles.sectionTitle} style={{ fontSize: "var(--text-sm)" }}>
        Before you escalate
      </h4>
      <p className={styles.hint}>
        Tick what you have already tried. Support receives this with the ticket, so they can skip the
        basics and get straight to the problem.
      </p>

      {steps === null && <p className={styles.hint}>Loading checklist…</p>}

      {steps !== null && steps.length > 0 && (
        <div className={styles.checkList} style={{ marginBottom: "var(--space-3)" }}>
          {steps.map((text, i) => (
            <label key={i} className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={tried[i] ?? false}
                onChange={(e) => setTried((prev) => prev.map((v, j) => (j === i ? e.target.checked : v)))}
              />
              {text}
            </label>
          ))}
        </div>
      )}

      <div className={styles.fieldRowStacked}>
        <label>What else did you try, and what happened?</label>
        <textarea
          className={styles.fieldTextarea}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="E.g. restarted the kiosk at 14:00, temperature still 12 °C…"
        />
      </div>

      {!canSend && !sending && (
        <p className={styles.hint}>Tick at least one step or write a note to continue.</p>
      )}
      {error && <div className={`${styles.statusMsg} ${styles.statusError}`}>{error}</div>}

      <div className={styles.inlineActions} style={{ marginTop: "var(--space-2)" }}>
        <button className={styles.secondaryBtn} onClick={onCancel} disabled={sending}>Cancel</button>
        <button className={styles.primaryBtn} onClick={handleSend} disabled={!canSend || steps === null}>
          {sending ? "Sending…" : "Send to Support"}
        </button>
      </div>
    </div>
  );
}
